import React from 'react';
import { AlertTriangleIcon, MailIcon, MapPinIcon, MessageCircleIcon, PhoneIcon, UserIcon } from 'lucide-react';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { ContactForm } from '../components/features/ContactForm';
import { CONTACT } from '../data/site';
import { IMAGES } from '../data/solutions';
import { localBusinessJsonLd, useSeo } from '../utils/seo';
import { trackCta } from '../utils/tracking';
const CRUMBS = [{
  name: 'Inicio',
  path: '/'
}, {
  name: 'Contacto',
  path: '/contacto'
}];
export function Contact() {
  useSeo({
    title: 'Contacto | Solicitar diagnóstico operativo — Rabemar Servicios Integrales',
    description: 'Cuéntenos qué necesita resolver. Diagnóstico operativo para logística, transporte, Facility Management, HSE, Control Tower, tecnología y sustentabilidad.',
    path: '/contacto',
    image: IMAGES.hero,
    breadcrumbs: CRUMBS,
    jsonLd: [localBusinessJsonLd]
  });
  return <>
      <section className="border-b border-mist-200 bg-white py-14 md:py-20" data-id="element-558" data-mp="15ff3lvimw764">
        <Container data-id="element-559" data-mp="15f6so962nbxk">
          <div className="max-w-3xl" data-id="element-560" data-mp="19h0nw1xhznqc">
            <Eyebrow data-id="element-561" data-mp="19fyzkvrh14qo">Contacto</Eyebrow>
            <Heading as="h1" size="lg" className="mt-5" data-id="element-562" data-mp="19exnqp2mse7w">
              Cuéntenos qué necesita resolver.
            </Heading>
            <p className="mt-6 text-lg leading-relaxed text-graphite-500" data-id="element-563" data-mp="1j6twcu2cfmqd">
              Describa la situación operativa y le devolvemos un alcance concreto: qué asume Rabemar, con qué procesos,
              qué indicadores se miden y cómo se controla el cumplimiento.
            </p>
          </div>
        </Container>
      </section>

      <section className="bg-mist-50 py-16 md:py-24" data-id="element-564" data-mp="vg7pv77zmaih">
        <Container data-id="element-565" data-mp="10gc9350a5jce">
          <div className="grid gap-12 lg:grid-cols-[1.25fr_0.75fr] lg:gap-16" data-id="element-566" data-mp="10gvghrsb0pc6">
            <ContactForm data-id="element-567" data-mp="26wei1kpvsy3c" />

            <div id="especialista" className="scroll-mt-24" data-id="element-568" data-mp="26x7i62uc4pci">
              <h2 className="font-display text-xl font-bold tracking-tight text-graphite-900" data-id="element-569" data-mp="26ysadlt6fw77">
                Hablar con un especialista
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-graphite-500" data-id="element-570" data-mp="1m6275btee3nq">
                Si prefiere una conversación directa, puede contactar al equipo comercial por teléfono, email o WhatsApp.
              </p>

              <ul className="mt-7 space-y-5" data-id="element-571" data-mp="1w4rs5dglc487">
                <li className="flex gap-3" data-id="element-572" data-mp="1r5wzzkwqh3bf">
                  <UserIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" data-id="element-573" data-mp="1r6zoxnahwvop" />
                  <span className="text-sm text-graphite-800" data-id="element-574" data-mp="13hdufd52j4n1">
                    <span className="block text-xs uppercase tracking-[0.12em] text-graphite-500" data-id="element-575" data-mp="yikcn8ir0rh2">Contacto comercial</span>
                    {CONTACT.commercialContact}
                  </span>
                </li>
                <li className="flex gap-3" data-id="element-576" data-mp="18exe4skjc9md">
                  <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" data-id="element-577" data-mp="18e449iqa5400" />
                  <span className="text-sm text-graphite-800" data-id="element-578" data-mp="ko1i1j91qs9b">
                    <span className="block text-xs uppercase tracking-[0.12em] text-graphite-500" data-id="element-579" data-mp="fqruk957wqo4">Teléfono</span>
                    <a href={CONTACT.phoneHref} onClick={() => trackCta('Llamar', 'contacto')} className="transition-colors duration-150 hover:text-brand-700" data-id="element-580" data-mp="2rdbvo5bxyz2f">
                      {CONTACT.phone}
                    </a>
                  </span>
                </li>
                <li className="flex gap-3" data-id="element-581" data-mp="3a4uwq6meo31t">
                  <MailIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" data-id="element-582" data-mp="3a5xk44kirncz" />
                  <span className="text-sm text-graphite-800" data-id="element-583" data-mp="3f3fw63tu2ip7">
                    <span className="block text-xs uppercase tracking-[0.12em] text-graphite-500" data-id="element-584" data-mp="313elxxrohyx2">Email</span>
                    <a href={`mailto:${CONTACT.email}`} onClick={() => trackCta('Email', 'contacto')} className="break-all transition-colors duration-150 hover:text-brand-700" data-id="element-585" data-mp="354qdw9y0yw8f">
                      {CONTACT.email}
                    </a>
                  </span>
                </li>
                <li className="flex gap-3" data-id="element-586" data-mp="256sk93tolg9c">
                  <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" aria-hidden="true" data-id="element-587" data-mp="255znmvofs74x" />
                  <span className="text-sm text-graphite-800" data-id="element-588" data-mp="208hd50urtjut">
                    <span className="block text-xs uppercase tracking-[0.12em] text-graphite-500" data-id="element-589" data-mp="1v9wdp3ncf47f">Dirección</span>
                    {CONTACT.address}
                  </span>
                </li>
              </ul>

              <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" onClick={() => trackCta('WhatsApp', 'contacto')} className="mt-8 inline-flex items-center gap-2 border border-brand-600 px-5 py-3.5 text-sm font-semibold text-brand-700 transition-colors duration-150 ease-smooth hover:bg-brand-600 hover:text-white" data-id="element-590" data-mp="2nz3iulhays1n">
                <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-591" data-mp="13gnktvf34z9q" />
                Escribir por WhatsApp
              </a>

              <p className="mt-8 flex gap-2 border border-accent-300 bg-accent-300/15 p-4 text-[11px] leading-relaxed text-accent-600" data-id="element-592" data-mp="3j6ma55jwiuck">
                <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-593" data-mp="3o56zub1j1vmr" />
                <span data-id="element-594" data-mp="3sxqmrym3u348">{CONTACT.validationNote}</span>
              </p>
            </div>
          </div>
        </Container>
      </section>
    </>;
}